import { createContext, type ReactNode, useContext, useState } from 'react';

import { MapPopover } from './MapPopover';

type OpenPopoverOptions = {
  render: () => ReactNode;
  x: number;
  y: number;
};

interface MapPopoverContextValue {
  closeMapPopover: () => void;
  isOpen: boolean;
  openMapPopover: (options: OpenPopoverOptions) => void;
}

const MapPopoverContext = createContext<MapPopoverContextValue | null>(null);

export function useMapPopover() {
  const ctx = useContext(MapPopoverContext);
  if (!ctx) {
    throw new Error('useMapPopover must be used within a <MapPopoverProvider>');
  }
  return ctx;
}

type MapPopoverProviderProps = {
  boundaryElement?: Element | null;
  children: ReactNode;
};

export function MapPopoverProvider({ boundaryElement, children }: MapPopoverProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [x, setX] = useState(0);
  const [y, setY] = useState(0);
  const [renderFn, setRenderFn] = useState<() => ReactNode>(() => () => null);

  const openMapPopover = ({ render, x: xParaam, y: yParam }: OpenPopoverOptions) => {
    setX(xParaam);
    setY(yParam);
    setRenderFn(() => render);
    setIsOpen(true);
  };

  const closeMapPopover = () => {
    setIsOpen(false);
  };

  return (
    <MapPopoverContext.Provider
      value={{
        closeMapPopover,
        isOpen,
        openMapPopover
      }}
    >
      {children}
      <MapPopover boundary={boundaryElement} open={isOpen} render={renderFn} x={x} y={y} />
    </MapPopoverContext.Provider>
  );
}
